import type {
  PaymentRequest,
  PaymentRequestStatus,
  SupplierPayoutNetwork,
  SupplierPayoutStatus,
} from 'helpers/fakeData';

export interface StatusMeta {
  label: string;
  palette: string;
}

export const PAYMENT_REQUEST_STATUS_META: Record<PaymentRequestStatus, StatusMeta> = {
  open: { label: 'Open', palette: 'blue' },
  partially_paid: { label: 'Partially paid', palette: 'yellow' },
  fulfilled: { label: 'Fulfilled', palette: 'green' },
  closed: { label: 'Closed', palette: 'gray' },
  cancelled: { label: 'Cancelled', palette: 'red' },
};

export const SUPPLIER_PAYOUT_STATUS_META: Record<SupplierPayoutStatus, StatusMeta> = {
  not_started: { label: 'Ready to pay', palette: 'blue' },
  pending: { label: 'Reserving funds', palette: 'yellow' },
  confirming: { label: 'Confirming on-chain', palette: 'yellow' },
  completed: { label: 'Delivered', palette: 'green' },
  held: { label: 'Held — needs attention', palette: 'red' },
};

export const SUPPLIER_PAYOUT_NETWORK_LABEL: Record<SupplierPayoutNetwork, string> = {
  stellar: 'Stellar',
  ethereum: 'Ethereum',
  base: 'Base',
  polygon: 'Polygon',
  arbitrum: 'Arbitrum',
  tron: 'Tron',
};

export const formatLocal = (amount: number, currency: string): string => {
  const formatted = amount.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `${currency} ${formatted}`;
};

export const formatUsdc = (amount: number): string => {
  const formatted = amount.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `${formatted} USDC`;
};

export const remainingAmount = (target: number, received: number): number => {
  return Math.max(target - received, 0);
};

// Indicative local-currency units per 1 USDC — for builder ESTIMATES ONLY. Real
// conversion locks per payment at spot, so the collected USDC is never exactly this.
const INDICATIVE_UNITS_PER_USDC: Record<string, number> = {
  GHS: 15.6,
  NGN: 1600,
  KES: 129,
  USD: 1,
};

export const estimateUsdc = (amount: number, currency: string): number | null => {
  const rate = INDICATIVE_UNITS_PER_USDC[currency];
  if (rate == null || rate <= 0 || !(amount > 0)) {
    return null;
  }
  return amount / rate;
};

export const isFullyPaid = (target: number, received: number): boolean => {
  return target > 0 && received >= target;
};

export const progressPercent = (target: number, received: number): number => {
  if (target <= 0) {
    return 0;
  }
  if (received >= target) {
    return 100;
  }
  // Floor so a not-yet-complete link never rounds up to 100% (which would
  // contradict a positive "remaining"); keep a 1% sliver visible once any
  // money has landed.
  const raw = (received / target) * 100;
  return Math.min(Math.max(Math.floor(raw), received > 0 ? 1 : 0), 99);
};

// A link is fully paid but its manual supplier payout hasn't been sent yet — the
// operator must trigger it. (Auto-settle links don't wait on the operator.)
// "Fully paid" is the collected total reaching the target, independent of the
// status label, so a paid-but-closed link still surfaces the pending payout.
export const isAwaitingManualPayout = (request: PaymentRequest): boolean => {
  return (
    isFullyPaid(request.targetAmount, request.receivedAmount) &&
    request.status !== 'cancelled' &&
    !request.autoSettle &&
    request.payout != null &&
    request.payout.status === 'not_started'
  );
};
